import React, { useCallback } from 'react';
import { ArtifactReadState } from '../../canvas/ArtifactReadState.jsx';
import CanvasRenderErrorBoundary from './CanvasRenderErrorBoundary.jsx';

const resolveCanvasWorkspacePanelModule = (module) => ({
  default: module.CanvasWorkspacePanel,
});

const isRetryableCanvasImportError = (error) => (
  /failed to fetch dynamically imported module|importing a module script failed|err_aborted/i.test(
    String(error?.message || error || ''),
  )
);

const loadCanvasWorkspacePanel = () => import('flare-canvas-ui')
  .then(resolveCanvasWorkspacePanelModule)
  .catch(async (error) => {
    if (!isRetryableCanvasImportError(error)) {
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 0));
    return import('flare-canvas-ui').then(resolveCanvasWorkspacePanelModule);
  });

const LazyCanvasWorkspacePanel = React.lazy(loadCanvasWorkspacePanel);

function CanvasPanelLoadingFallback({ themeTokens = {} }) {
  return (
    <div
      aria-live="polite"
      role="status"
      style={{
        alignItems: 'center',
        background: themeTokens.surfaceBg || '#ffffff',
        color: themeTokens.textSecondary || '#64748b',
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        minHeight: 180,
        padding: 24,
      }}
    >
      正在打开工作台…
    </div>
  );
}

/**
 * Render workspace canvas section with error boundary protection.
 *
 * @param {object} props - Canvas section render props.
 * @returns {JSX.Element} Canvas panel node.
 */
export default function ChatWorkspaceCanvasSection(props) {
  const {
    artifactReadState,
    canvasPanelErrorFallback,
    handleCanvasRenderError,
    canvasActiveTabKey,
    showCanvasPanel,
    canvasArtifactDocument,
    canvasArtifactTransition,
    canvasCollectedCount,
    canvasDisplayLines,
    canvasDraftEditing,
    canvasDraftEditorValue,
    canvasHighlights,
    resolvedUILabels,
    canvasFieldRows,
    canvasRevisionEntries,
    canvasRevisionControls,
    canvasFullscreenOpen,
    canvasManualControlsEnabled,
    canvasProgressPercent,
    canvasWorkflowProjection,
    canvasTabs,
    canvasEvidenceTabKey,
    canvasEvidenceSummary,
    canvasEvidenceItems,
    canvasFocusEvidenceAnchorId,
    canvasSourcingLoading,
    canvasSourcingHasMore,
    canvasSourcingLoadingMore,
    onCanvasSourcingLoadMore,
    canvasReportTemplate,
    canvasAnalysisPending,
    canvasContentSettling,
    resolvedCanvasAnalysisPayload,
    inputState,
    canvasWorkspaceFullscreenMode,
    isCompactLayout,
    handleCanvasApplyRevision,
    setCanvasActiveTabKey,
    setCanvasDraftEditorValue,
    handleCanvasCancelEdit,
    setCanvasFullscreenOpen,
    setCanvasPanelOpen,
    handleCanvasSaveEdit,
    handleCanvasStartEdit,
    handleCanvasToolbarAction,
    themeTokens,
  } = props;
  const clearSelection = artifactReadState?.clear;
  const handleClose = useCallback(() => {
    clearSelection?.();
    setCanvasFullscreenOpen(false);
    setCanvasPanelOpen(false);
  }, [clearSelection, setCanvasFullscreenOpen, setCanvasPanelOpen]);
  return (
    <CanvasRenderErrorBoundary
      fallback={canvasPanelErrorFallback}
      onError={handleCanvasRenderError}
      resetKey={`${canvasActiveTabKey}:${showCanvasPanel ? 'open' : 'closed'}`}
    >
      {showCanvasPanel ? (
        <ArtifactReadState state={artifactReadState} onClose={handleClose}>
        <React.Suspense fallback={<CanvasPanelLoadingFallback themeTokens={themeTokens} />}>
          <LazyCanvasWorkspacePanel
            canvasArtifactDocument={canvasArtifactDocument}
            canvasArtifactTransition={canvasArtifactTransition}
            canvasCollectedCount={canvasCollectedCount}
            canvasDisplayLines={canvasDisplayLines}
            canvasDraftEditing={canvasDraftEditing}
            canvasDraftEditorValue={canvasDraftEditorValue}
            canvasEmptyResultLabel={resolvedUILabels.canvas_empty_result || '发送后在这里生成需求草稿。'}
            canvasFieldRows={canvasFieldRows}
            canvasHighlights={canvasHighlights}
            canvasRevisionEntries={canvasRevisionEntries}
            canvasRevisionControls={canvasRevisionControls}
            canvasFieldRowsAuthoritative
            canvasFullscreenOpen={canvasFullscreenOpen}
            canvasWorkspaceFullscreenMode={canvasWorkspaceFullscreenMode}
            canvasManualControlsEnabled={canvasManualControlsEnabled}
            canvasProgressPercent={canvasProgressPercent}
            canvasWorkflowProgressVisible={false}
            canvasWorkflowProjection={canvasWorkflowProjection}
            canvasTabs={canvasTabs}
            canvasActiveTabKey={canvasActiveTabKey}
            canvasEvidenceTabKey={canvasEvidenceTabKey}
            canvasEvidenceSummary={canvasEvidenceSummary}
            canvasEvidenceItems={canvasEvidenceItems}
            canvasFocusEvidenceAnchorId={canvasFocusEvidenceAnchorId}
            canvasSourcingLoading={canvasSourcingLoading}
            canvasSourcingHasMore={canvasSourcingHasMore}
            canvasSourcingLoadingMore={canvasSourcingLoadingMore}
            onCanvasSourcingLoadMore={onCanvasSourcingLoadMore}
            canvasReportTemplate={canvasReportTemplate}
            canvasAnalysisPending={canvasAnalysisPending}
            canvasContentSettling={canvasContentSettling}
            canvasAnalysisPayload={resolvedCanvasAnalysisPayload}
            canvasRevisionApplyLabel={resolvedUILabels.canvas_revision_apply || '应用修改'}
            canvasRevisionInput={inputState.inputValue}
            canvasStatusTitle={resolvedUILabels.canvas_status_title || '状态'}
            canvasTabResultTitle={resolvedUILabels.canvas_tab_result || '工作内容'}
            canvasWorkspaceTitle={resolvedUILabels.canvas_workspace_title || '工作台'}
            showRevisionComposer={canvasWorkspaceFullscreenMode}
            compact={isCompactLayout}
            onApplyRevision={handleCanvasApplyRevision}
            onCanvasTabChange={setCanvasActiveTabKey}
            onCanvasDraftEditorValueChange={setCanvasDraftEditorValue}
            onCancelEdit={handleCanvasCancelEdit}
            onClose={handleClose}
            onSaveEdit={handleCanvasSaveEdit}
            onStartEdit={handleCanvasStartEdit}
            onToolbarAction={handleCanvasToolbarAction}
            onToggleFullscreen={() => setCanvasFullscreenOpen((prev) => !prev)}
            themeTokens={themeTokens}
            visible={showCanvasPanel}
          />
        </React.Suspense>
        </ArtifactReadState>
      ) : null}
    </CanvasRenderErrorBoundary>
  );
}
